import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, MapPin } from 'lucide-react';
import PageHero from '../components/PageHero';
import { Reveal } from '../components/Reveal';
import { api } from '../lib/api';

const PAST = [
    ['World Economic Forum Annual Meeting', 'Davos', 'Delegation design & media program'],
    ['UN General Assembly Week', 'New York', 'Side-forum activation & bilateral scheduling'],
    ['Global Health Summit', 'Berlin', 'Keynote placement & press office'],
    ['Sovereign Investment Forum', 'Abu Dhabi', 'LP introductions & partnership curation'],
];

export default function Events() {
    const [events, setEvents] = useState(null);
    const [filter, setFilter] = useState('All');

    useEffect(() => {
        api.get('/events')
            .then((r) => setEvents(r.data))
            .catch(() => setEvents([]));
    }, []);

    const categories = useMemo(
        () => ['All', ...new Set((events || []).map((e) => e.category))],
        [events]
    );
    const shown = useMemo(
        () => (events || []).filter((e) => filter === 'All' || e.category === filter),
        [events, filter]
    );

    return (
        <>
            <PageHero
                overline="Event Activations"
                lines={['Where your market', 'convenes,', 'we are present.']}
                sub="CommPro represents clients at the summits, forums, and assemblies where decisions are made — managing delegations, securing stages, and activating partnerships on the ground."
            />

            <section className="py-20 sm:py-24 border-t border-line">
                <div className="max-w-7xl mx-auto px-5 sm:px-8">
                    <Reveal className="flex flex-wrap items-center gap-3">
                        {categories.map((c) => (
                            <button
                                key={c}
                                data-testid={`event-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                                onClick={() => setFilter(c)}
                                className={`font-mono text-[10px] uppercase tracking-[0.2em] px-5 py-2.5 border transition-colors duration-300 ${
                                    filter === c
                                        ? 'border-bronze text-bronze'
                                        : 'border-line text-fog hover:text-cream hover:border-fog'
                                }`}
                            >
                                {c}
                            </button>
                        ))}
                    </Reveal>

                    <div data-testid="events-catalog" className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-px bg-line border border-line">
                        {shown === null
                            ? Array.from({ length: 4 }).map((_, i) => (
                                  <div key={i} className="bg-ink h-56 animate-pulse" />
                              ))
                            : shown.map((e, i) => (
                                  <motion.article
                                      key={e.id}
                                      data-testid="event-card"
                                      initial={{ opacity: 0, y: 24 }}
                                      whileInView={{ opacity: 1, y: 0 }}
                                      viewport={{ once: true }}
                                      transition={{ duration: 0.7, delay: (i % 2) * 0.08, ease: [0.22, 1, 0.36, 1] }}
                                      className="group bg-ink hover:bg-surface/60 transition-colors duration-500 p-8 flex flex-col min-h-[15rem]"
                                  >
                                      <div className="flex items-center justify-between">
                                          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-bronze">
                                              {e.date_label}
                                          </span>
                                          <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-fog border border-line px-3 py-1">
                                              {e.category}
                                          </span>
                                      </div>
                                      <h2 className="mt-5 font-serif text-2xl text-cream group-hover:italic transition-all duration-300">
                                          {e.title}
                                      </h2>
                                      <p className="mt-3 text-sm text-fog leading-relaxed flex-1">{e.description}</p>
                                      <p className="mt-5 inline-flex items-center gap-2 text-sm text-fog">
                                          <MapPin size={13} className="text-bronze" />
                                          {e.location}
                                      </p>
                                  </motion.article>
                              ))}
                    </div>
                </div>
            </section>

            <section className="dark-section py-20 sm:py-28 border-t border-line">
                <div className="max-w-7xl mx-auto px-5 sm:px-8">
                    <Reveal>
                        <p className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.3em] text-bronze flex items-center gap-3">
                            <span className="inline-block w-10 h-px bg-bronze" />
                            Track Record
                        </p>
                        <h2 className="mt-5 font-serif text-3xl sm:text-4xl font-medium tracking-tight text-cream">
                            Past global <span className="text-bronze">delegations & panels.</span>
                        </h2>
                    </Reveal>
                    <div className="mt-12 border-t border-line">
                        {PAST.map(([title, place, role], i) => (
                            <Reveal key={title} delay={i * 0.05}>
                                <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-6 py-6 border-b border-line px-2 items-baseline">
                                    <h3 className="sm:col-span-5 font-serif text-xl text-cream">{title}</h3>
                                    <span className="sm:col-span-3 font-mono text-[11px] uppercase tracking-[0.2em] text-bronze">
                                        {place}
                                    </span>
                                    <span className="sm:col-span-4 text-sm text-fog">{role}</span>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.1}>
                        <Link
                            to="/contact?interest=Event%20Participation%20%26%20Activation"
                            data-testid="event-register-button"
                            className="group mt-12 inline-flex items-center gap-3 bg-bronze text-ink font-mono text-[11px] uppercase tracking-[0.22em] px-10 py-4 rounded-full relative overflow-hidden"
                        >
                            <span className="absolute inset-0 bg-gold translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out" />
                            <span className="relative">Request Representation</span>
                            <ArrowRight size={14} className="relative group-hover:translate-x-1 transition-transform duration-300" />
                        </Link>
                    </Reveal>
                </div>
            </section>
        </>
    );
}
